/**
 * 🚨 ISSUE SERVICE — Gestão de Problemas
 *
 * Serviço para o módulo Problemas (Issues):
 *  - CRUD de issues (incidentes, não-conformidades, desvios, reclamações, falhas)
 *  - Filtragem por organização, severidade, status, tipo
 *  - Transições de status com validação
 *
 * Regras:
 *  - Sempre filtrar por organization_id
 *  - Nunca chamar Supabase direto em componentes
 *  - Respeitar RLS e multi-tenancy
 */

import { supabase } from '../lib/supabaseClient';
import type { Issue, IssueType, IssueSeverity, IssueStatus, IssueMode, RootCauseAnalysis, ValidationConfig, MonitoringConfig, RecurrenceMatch, ProblemAlert, IssueMetadata } from '../types/issue';
import { getStatusTransitions, ISSUE_STATUS_LABELS, isTransitionAllowed, transitionRefusal } from '../types/issue';
import { causeHistoryMessage, structuredCauseGate } from '../utils/problemCause';
import { resolveIssueMode } from '../utils/resolveIssueMode';
import { appendToHistory, createHistoryEntry, reopenIssueMetadata } from '../utils/issueHistory';
import { getIssueOrigin } from '../utils/issueOrigin';
import {
  canCloseIssue,
  canConfirmEfficacy,
  efficacyAttempts,
  readyForEfficacyCheck,
  recordEfficacyAttempt,
  statusAfterIneffectiveCheck,
} from '../utils/efficacyCheck';
import type { EnterpriseAction } from './actionService';
import { removeIssueEvidence } from './issueEvidenceService';

export interface IssueFilters {
  type?: IssueType;
  severity?: IssueSeverity;
  status?: IssueStatus;
  mode?: IssueMode;
  assigned_to?: string;
  project_id?: string;
  department?: string;
  search?: string;
  plan_id?: string;
}

export class IssueService {
  static async list(organizationId: string, filters?: IssueFilters): Promise<Issue[]> {
    let query = supabase
      .from('issues')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (filters?.type) query = query.eq('type', filters.type);
    if (filters?.severity) query = query.eq('severity', filters.severity);
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.mode) query = query.eq('mode', filters.mode);
    if (filters?.assigned_to) query = query.eq('assigned_to', filters.assigned_to);
    if (filters?.project_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(filters.project_id)) query = query.eq('project_id', filters.project_id);
    if (filters?.department) query = query.eq('department', filters.department);
    if (filters?.search) query = query.ilike('title', `%${filters.search}%`);
    if (filters?.plan_id) query = query.eq('plan_id', filters.plan_id);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as Issue[];
  }

  static async getById(organizationId: string, issueId: string): Promise<Issue | null> {
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', issueId)
      .single();

    if (error) throw error;
    return data as Issue | null;
  }

  static async create(
    organizationId: string,
    issue: Partial<Omit<Issue, 'id' | 'organization_id' | 'created_at' | 'updated_at'>>,
    opts?: { actorId?: string; actorName?: string; recurrenceDetected?: boolean; forceStructured?: boolean },
  ): Promise<Issue> {
    const recurrenceDetected = opts?.recurrenceDetected ?? issue.metadata?.recurrence_detected ?? false;
    const forceStructured = opts?.forceStructured ?? issue.metadata?.force_structured ?? false;
    const mode = issue.mode ?? resolveIssueMode({
      type: issue.type ?? 'incident',
      severity: issue.severity ?? 'medium',
      recurrenceDetected,
      forceStructured,
    });

    const originView = getIssueOrigin({
      organizationId,
      source: issue.source,
      projectId: issue.project_id,
      metadata: issue.metadata,
    });
    const originNote = originView.kind === 'unrecorded' ? '' : ` Origem: ${originView.label}.`;
    const metadata = appendToHistory(issue.metadata ?? {}, createHistoryEntry(
      'created',
      `Problema criado (${mode === 'quick_correction' ? 'correção rápida' : 'estruturado'}).${originNote}`,
      { actor_id: opts?.actorId, actor_name: opts?.actorName },
    ));

    const payload = {
      ...issue,
      mode,
      metadata: {
        ...metadata,
        recurrence_detected: recurrenceDetected,
        force_structured: forceStructured,
      },
    };

    const { data, error } = await supabase
      .from('issues')
      .insert({ ...payload, organization_id: organizationId })
      .select()
      .single();

    if (error) throw error;
    return data as Issue;
  }

  static async update(
    organizationId: string,
    issueId: string,
    updates: Partial<Issue>,
    opts?: { actorId?: string; actorName?: string; historyMessage?: string; historyType?: 'status_changed' | 'assigned' | 'note' },
  ): Promise<Issue> {
    let payload = { ...updates };

    if (updates.status === 'closed') {
      await this.assertCloseAllowed(
        organizationId,
        issueId,
        (updates.metadata as IssueMetadata | undefined)?.closure_reason,
      );
    }

    if (opts?.historyMessage) {
      const current = await this.getById(organizationId, issueId);
      const historyType = opts.historyType ?? (updates.assigned_to !== undefined ? 'assigned' : 'note');
      const metadata = appendToHistory(
        (payload.metadata as IssueMetadata | undefined) ?? current?.metadata ?? {},
        createHistoryEntry(historyType, opts.historyMessage, {
          actor_id: opts.actorId,
          actor_name: opts.actorName,
        }),
      );
      payload = { ...payload, metadata };
    } else if (updates.assigned_to !== undefined) {
      const current = await this.getById(organizationId, issueId);
      if (current && current.assigned_to !== updates.assigned_to) {
        const metadata = appendToHistory(
          current.metadata ?? {},
          createHistoryEntry('assigned', updates.assigned_to ? 'Responsável definido' : 'Responsável removido'),
        );
        payload = { ...payload, metadata };
      }
    }

    const { data, error } = await supabase
      .from('issues')
      .update(payload)
      .eq('organization_id', organizationId)
      .eq('id', issueId)
      .select()
      .single();

    if (error) throw error;
    return data as Issue;
  }

  static async delete(organizationId: string, issueId: string): Promise<void> {
    await removeIssueEvidence(organizationId, issueId);
    const { error } = await supabase
      .from('issues')
      .delete()
      .eq('organization_id', organizationId)
      .eq('id', issueId);

    if (error) throw error;
  }

  private static async assertCloseAllowed(
    organizationId: string,
    issueId: string,
    closureReason?: string | null,
  ): Promise<void> {
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');
    const actions = await this.listDirectCountermeasures(organizationId, issueId);
    const decision = canCloseIssue({
      actions: actions.map((action) => ({
        id: action.id,
        status: action.status,
        completed_at: action.completed_at,
      })),
      attempts: efficacyAttempts(issue.metadata),
      closureReason: closureReason ?? issue.metadata?.closure_reason,
      metadata: issue.metadata,
    });
    if (decision.allowed === false) throw new Error(decision.reason);
  }

  private static assertCanConfirm(
    issue: Issue,
    actor?: { id?: string; name?: string; role?: string | null },
  ): void {
    const decision = canConfirmEfficacy({
      severity: issue.severity,
      verifierId: issue.metadata?.verification_owner_id,
      problemOwnerId: issue.assigned_to,
      actorId: actor?.id,
      actorRole: actor?.role,
    });
    if (decision.allowed === false) throw new Error(decision.reason);
  }

  static async transitionStatus(
    organizationId: string,
    issueId: string,
    newStatus: IssueStatus,
    opts?: { closureReason?: string; reopenReason?: string; actorId?: string; actorName?: string },
  ): Promise<Issue> {
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');

    if (!isTransitionAllowed(issue.mode, issue.status, newStatus)) {
      throw new Error(transitionRefusal(issue.mode, issue.status, newStatus));
    }
    const causeGate = structuredCauseGate(issue, newStatus);
    if (causeGate) throw new Error(causeGate);

    const updates: Partial<Issue> = { status: newStatus };
    let historyMessage = `Status alterado para ${ISSUE_STATUS_LABELS[newStatus]}`;
    let historyType: 'status_changed' | 'assigned' | 'note' = 'status_changed';

    if (issue.status === 'closed' && newStatus !== 'closed') {
      updates.metadata = reopenIssueMetadata(issue.metadata, {
        reason: opts?.reopenReason ?? '',
        actorId: opts?.actorId,
        actorName: opts?.actorName,
      });
      historyMessage = '';
    }

    if (newStatus === 'closed') {
      const reason = opts?.closureReason?.trim() || issue.metadata?.closure_reason;
      await this.assertCloseAllowed(organizationId, issueId, reason);
      updates.resolved_at = new Date().toISOString();
      if (opts?.closureReason?.trim()) {
        updates.metadata = { ...(issue.metadata ?? {}), closure_reason: opts.closureReason.trim() };
      }
      historyMessage = 'Problema encerrado';
    }

    return this.update(organizationId, issueId, updates, {
      actorId: opts?.actorId,
      actorName: opts?.actorName,
      historyType,
      historyMessage: historyMessage || undefined,
    });
  }

  static async appendHistory(
    organizationId: string,
    issueId: string,
    message: string,
    type: 'note' | 'action_linked' | 'project_linked' | 'workflow_linked' | 'goal_linked' | 'validation_started' | 'efficacy_check' | 'opportunity_linked' | 'reopened' = 'note',
    opts?: { actorId?: string; actorName?: string; meta?: Record<string, unknown> },
  ): Promise<Issue> {
    const current = await this.getById(organizationId, issueId);
    if (!current) throw new Error('Issue not found');
    const metadata = appendToHistory(
      current.metadata ?? {},
      createHistoryEntry(type, message, {
        actor_id: opts?.actorId,
        actor_name: opts?.actorName,
        meta: opts?.meta,
      }),
    );
    return this.update(organizationId, issueId, { metadata });
  }

  static async linkProject(organizationId: string, issueId: string, projectId: string): Promise<Issue> {
    return this.update(organizationId, issueId, { project_id: projectId }, {
      historyType: 'note',
      historyMessage: 'Projeto vinculado',
    });
  }

  static async linkWorkflow(organizationId: string, issueId: string, workflowId: string): Promise<Issue> {
    return this.update(organizationId, issueId, { workflow_id: workflowId }, {
      historyType: 'note',
      historyMessage: 'Workflow vinculado',
    });
  }

  static async linkHoshinGoal(organizationId: string, issueId: string, goalId: string): Promise<Issue> {
    const current = await this.getById(organizationId, issueId);
    const metadata: IssueMetadata = {
      ...(current?.metadata ?? {}),
      linked_hoshin_goal_id: goalId,
    };
    return this.update(organizationId, issueId, { metadata }, {
      historyType: 'note',
      historyMessage: 'Meta estratégica vinculada',
    });
  }

  static async getCountByStatus(organizationId: string): Promise<Record<IssueStatus, number>> {
    const { data, error } = await supabase
      .from('issues')
      .select('status')
      .eq('organization_id', organizationId);

    if (error) throw error;

    const counts: Record<string, number> = {
      open: 0, investigation: 0, root_cause: 0, action_plan: 0,
      execution: 0, validation: 0, monitoring: 0, closed: 0,
    };
    for (const row of data ?? []) {
      counts[row.status] = (counts[row.status] || 0) + 1;
    }
    return counts as Record<IssueStatus, number>;
  }

  /** Save root cause analysis data (JSONB column) */
  static async saveRootCauseAnalysis(
    organizationId: string,
    issueId: string,
    analysis: RootCauseAnalysis,
  ): Promise<Issue> {
    const current = await this.getById(organizationId, issueId);
    const confirming = analysis.cause_confirmed === true && Boolean(analysis.root_cause_description?.trim());
    const result = await this.update(organizationId, issueId, {
      root_cause_analysis: analysis,
      root_cause: confirming ? analysis.root_cause_description.trim() : current?.root_cause,
    } as Partial<Issue>, {
      historyType: 'note',
      historyMessage: causeHistoryMessage(current?.root_cause_analysis, analysis),
    });
    return result;
  }

  /** Get linked actions count for an issue */
  static async getLinkedActionsCount(organizationId: string, issueId: string): Promise<number> {
    const actions = await this.listLinkedActions(organizationId, issueId);
    return actions.length;
  }

  /** Contramedida direta: somente actions.problem_id. Metadata e oportunidade ficam de fora. */
  static async listDirectCountermeasures(organizationId: string, issueId: string): Promise<EnterpriseAction[]> {
    return this.listLinkedActions(organizationId, issueId);
  }

  /** List actions linked to an issue by actions.problem_id. */
  static async listLinkedActions(organizationId: string, issueId: string): Promise<EnterpriseAction[]> {
    const { data, error } = await supabase
      .from('actions')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('problem_id', issueId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return ((data ?? []) as EnterpriseAction[]).filter((action) => action.problem_id === issueId);
  }

  /** Sync denormalized linked_actions_count on issue */
  static async syncLinkedActionsCount(organizationId: string, issueId: string): Promise<void> {
    const count = await this.getLinkedActionsCount(organizationId, issueId);
    await supabase
      .from('issues')
      .update({ linked_actions_count: count })
      .eq('organization_id', organizationId)
      .eq('id', issueId);
  }

  /** Get department breakdown for dashboard */
  static async getCountByDepartment(organizationId: string): Promise<Record<string, number>> {
    const { data, error } = await supabase
      .from('issues')
      .select('department')
      .eq('organization_id', organizationId)
      .neq('status', 'closed');

    if (error) throw error;

    const counts: Record<string, number> = {};
    for (const row of data ?? []) {
      const dept = (row as { department: string | null }).department || 'Sem departamento';
      counts[dept] = (counts[dept] || 0) + 1;
    }
    return counts;
  }

  // ─── Validation ────────────────────────────────────

  /** Save validation configuration (JSONB column) */
  static async saveValidationConfig(
    organizationId: string,
    issueId: string,
    config: ValidationConfig,
  ): Promise<Issue> {
    const isNew = !config.started_at;
    const payload = { ...config, started_at: config.started_at ?? new Date().toISOString() };
    return this.update(organizationId, issueId, { validation_config: payload } as Partial<Issue>, {
      historyType: 'note',
      historyMessage: isNew ? 'Validação iniciada' : 'Configuração de validação atualizada',
    });
  }

  /** Add a manual inspection to the validation */
  static async addManualInspection(
    organizationId: string,
    issueId: string,
    inspection: { inspection_date: string; notes: string; inspector_id?: string },
  ): Promise<Issue> {
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');

    const config: ValidationConfig = issue.validation_config ?? { mode: 'manual', inspections: [] };
    const inspections = config.inspections ?? [];
    inspections.push(inspection);
    config.inspections = inspections;

    return this.update(organizationId, issueId, { validation_config: config } as Partial<Issue>);
  }

  /**
   * Confirmação humana de eficácia. Acrescenta a tentativa e não encerra o problema.
   * Ação concluída e eficácia confirmada continuam diferentes de problema resolvido.
   */
  static async confirmEfficacy(
    organizationId: string,
    issueId: string,
    note: string,
    actor?: { id?: string; name?: string; role?: string | null },
  ): Promise<Issue> {
    const trimmed = note.trim();
    if (!trimmed) throw new Error('Informe a observação da verificação');
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');
    this.assertCanConfirm(issue, actor);
    const actions = await this.listDirectCountermeasures(organizationId, issueId);
    if (!readyForEfficacyCheck(actions, issue.status)) {
      throw new Error('Ainda há contramedida aberta');
    }

    const completedIds = actions.filter((action) => action.status === 'completed').map((action) => action.id);
    const metadata = recordEfficacyAttempt(issue.metadata, {
      result: 'effective',
      note: trimmed,
      at: new Date().toISOString(),
      actor_id: actor?.id ?? null,
      actor_name: actor?.name ?? null,
      action_ids: completedIds,
    });

    return this.update(organizationId, issueId, { metadata });
  }

  /**
   * A contramedida foi executada e a condição continua.
   * Ações concluídas permanecem concluídas. O problema não encerra.
   */
  static async rejectEfficacy(
    organizationId: string,
    issueId: string,
    note: string,
    actor?: { id?: string; name?: string; role?: string | null },
  ): Promise<Issue> {
    const trimmed = note.trim();
    if (!trimmed) throw new Error('Informe por que a verificação não foi eficaz');
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');
    this.assertCanConfirm(issue, actor);
    const actions = await this.listDirectCountermeasures(organizationId, issueId);
    if (!readyForEfficacyCheck(actions, issue.status)) {
      throw new Error('Ainda há contramedida aberta');
    }

    const completedIds = actions.filter((action) => action.status === 'completed').map((action) => action.id);
    const metadata = recordEfficacyAttempt(issue.metadata, {
      result: 'ineffective',
      note: trimmed,
      at: new Date().toISOString(),
      actor_id: actor?.id ?? null,
      actor_name: actor?.name ?? null,
      action_ids: completedIds,
    });
    const nextStatus = statusAfterIneffectiveCheck(issue.status);
    const updated = await this.update(organizationId, issueId, { metadata });
    if (nextStatus !== issue.status) {
      const allowed = getStatusTransitions(issue.mode)[issue.status] ?? [];
      if (allowed.includes(nextStatus)) {
        return this.transitionStatus(organizationId, issueId, nextStatus);
      }
    }
    return updated;
  }

  /** Mark validation as complete with result */
  static async completeValidation(
    organizationId: string,
    issueId: string,
    result: 'passed' | 'failed',
  ): Promise<Issue> {
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');

    const config: ValidationConfig = issue.validation_config ?? { mode: 'manual' };
    config.completed_at = new Date().toISOString();
    config.result = result;

    return this.update(organizationId, issueId, { validation_config: config } as Partial<Issue>);
  }

  // ─── Monitoring ────────────────────────────────────

  /** Save monitoring configuration (JSONB column) */
  static async saveMonitoringConfig(
    organizationId: string,
    issueId: string,
    config: MonitoringConfig,
  ): Promise<Issue> {
    return this.update(organizationId, issueId, {
      monitoring_config: config,
    } as Partial<Issue>);
  }

  /** Record a monitoring check */
  static async recordMonitoringCheck(
    organizationId: string,
    issueId: string,
  ): Promise<Issue> {
    const issue = await this.getById(organizationId, issueId);
    if (!issue) throw new Error('Issue not found');

    const config: MonitoringConfig = issue.monitoring_config ?? { mode: 'manual' };
    config.last_check_at = new Date().toISOString();
    config.checks_count = (config.checks_count ?? 0) + 1;

    return this.update(organizationId, issueId, { monitoring_config: config } as Partial<Issue>);
  }

  // ─── Recurrence Detection ─────────────────────────

  /** Detect potential recurrences for a new issue based on similarity */
  static async detectRecurrence(
    organizationId: string,
    newIssue: { type: IssueType; process?: string | null; step?: string | null; root_cause?: string | null },
  ): Promise<RecurrenceMatch[]> {
    // Fetch closed issues from same org
    const { data, error } = await supabase
      .from('issues')
      .select('id, title, type, process, step, root_cause, created_at')
      .eq('organization_id', organizationId)
      .in('status', ['closed', 'monitoring']);

    if (error || !data) return [];

    const matches: RecurrenceMatch[] = [];

    for (const existing of data) {
      const matchingFields: string[] = [];
      let score = 0;

      if (existing.type === newIssue.type) {
        matchingFields.push('type');
        score += 0.25;
      }
      if (newIssue.process && existing.process && existing.process === newIssue.process) {
        matchingFields.push('process');
        score += 0.30;
      }
      if (newIssue.step && existing.step && existing.step === newIssue.step) {
        matchingFields.push('step');
        score += 0.20;
      }
      if (newIssue.root_cause && existing.root_cause &&
          existing.root_cause.toLowerCase().includes(newIssue.root_cause.toLowerCase().substring(0, 30))) {
        matchingFields.push('root_cause');
        score += 0.25;
      }

      if (score >= 0.50 && matchingFields.length >= 2) {
        matches.push({
          issue_id: existing.id,
          issue_title: existing.title,
          similarity_score: Math.round(score * 100) / 100,
          matching_fields: matchingFields,
          created_at: existing.created_at,
        });
      }
    }

    return matches.sort((a, b) => b.similarity_score - a.similarity_score).slice(0, 5);
  }

  // ─── Alerts ────────────────────────────────────────

  /** Generate problem alerts for current issues */
  static async generateAlerts(organizationId: string): Promise<ProblemAlert[]> {
    const alerts: ProblemAlert[] = [];
    const now = Date.now();

    // Fetch issues in monitoring/validation status
    const { data: activeIssues, error } = await supabase
      .from('issues')
      .select('*')
      .eq('organization_id', organizationId)
      .in('status', ['monitoring', 'validation']);

    if (error || !activeIssues) return alerts;

    for (const issue of activeIssues as Issue[]) {
      // Check monitoring failures (no check in 7+ days)
      if (issue.status === 'monitoring' && issue.monitoring_config) {
        const lastCheck = issue.monitoring_config.last_check_at;
        if (lastCheck) {
          const daysSinceCheck = (now - new Date(lastCheck).getTime()) / (1000 * 60 * 60 * 24);
          if (daysSinceCheck > 7) {
            alerts.push({
              id: `mf-${issue.id}`,
              type: 'monitoring_failure',
              message: `"${issue.title}" sem verificação há ${Math.floor(daysSinceCheck)} dias`,
              related_issue_id: issue.id,
              created_at: new Date().toISOString(),
            });
          }
        }
      }

      // Check time-based validation expiry
      if (issue.status === 'validation' && issue.validation_config?.mode === 'time') {
        const startedAt = issue.validation_config.started_at;
        const validationDays = issue.validation_config.time?.validation_days ?? 30;
        if (startedAt) {
          const elapsed = (now - new Date(startedAt).getTime()) / (1000 * 60 * 60 * 24);
          if (elapsed > validationDays) {
            alerts.push({
              id: `tv-${issue.id}`,
              type: 'monitoring_failure',
              message: `Validação por tempo expirou para "${issue.title}" (${validationDays}d)`,
              related_issue_id: issue.id,
              created_at: new Date().toISOString(),
            });
          }
        }
      }
    }

    return alerts;
  }
}
